import React, {
  useRef,
  useEffect,
  useState,
  useContext,
  lazy,
  Suspense,
} from 'react';
import _ from 'lodash';
import { Modal } from '@wix/design-system';

import { DocumentServicesContext } from '../App/DocumentServicesContext';
import { ViewModeContext } from '../App/ViewModeContext';
import { ResponsiveSiteContext } from '../App/ResponsiveSiteContext';
import { BreakpointsContext } from '../App/BreakpointsContext';

import {
  VIEW_MODE,
  VIEW_MODE_DOCUMENT_VALUES,
  DOCUMENT_SERVICES_LOADED,
  DOCUMENT_HEADER,
  PREVIEW_ID,
  DOCUMENT_CONTAINER,
  COMMENT_COLOR,
  SITE_LOADING_GRACE,
  COMMENT_STATUS,
  BREAKPOINT_MAX_VALUE,
} from '../../utils/constants';
import * as documentAPIs from '../../utils/documentAPI';
import { siteUrl } from '../../fromWindow';
import { getScrollOffset } from './frameHelpers';
import Portal from './Portal';
import feedbackPinStyle from '../FeedbackPin/FeedbackPinStyle';
import s from './SiteRenderer.scss';
import { ACTIONS, useAppContext } from '../App/AppContext';
import {
  calcCommentInnerPathFromPageSlug,
  isDynamicPage,
} from '../../utils/pagesUtils';

const FeedbackPin = lazy(() => import('../FeedbackPin/FeedbackPin'));

function SiteRenderer({
  comments,
  pageId,
  siteDidNotRender,
  onUpdateComment,
  onResolveComment,
  onAppendOwnerReply,
  onDeleteReplyToComment,
  onUpdateReplyToComment,
  closeAllComments,
  onMarkCommentAsRead,
  mockDocumentServices,
  resolvedFilterOn,
  openFilterOn,
}) {
  const { documentServices, setDocumentServices, setIframeElement } =
    useContext(DocumentServicesContext);
  const {
    state: { showOverlay },
    dispatch,
  } = useAppContext();
  const documentServicesRef = useRef(documentServices);
  const { viewMode } = useContext(ViewModeContext);
  const { isSiteResponsive } = useContext(ResponsiveSiteContext);
  const { currentBreakPoint } = useContext(BreakpointsContext);
  const [loading, setLoading] = useState(true);
  const previewEl = useRef(null);
  const [commentTypesToShow, setCommentTypesToShow] = useState([
    COMMENT_STATUS.UNREAD,
    COMMENT_STATUS.OPEN,
  ]);

  function getCurrentPageInnerPath() {
    const { relativeUrl, pageAdditionalData } =
      documentServicesRef?.current?.pages?.getRootNavigationInfo() || {};

    return pageAdditionalData
      ? pageAdditionalData
      : relativeUrl?.substring(2) || '';
  }

  const currentPageInnerPath = getCurrentPageInnerPath();

  useEffect(() => {
    if (openFilterOn && resolvedFilterOn) {
      setCommentTypesToShow([
        COMMENT_STATUS.UNREAD,
        COMMENT_STATUS.OPEN,
        COMMENT_STATUS.RESOLVED,
      ]);
      return;
    }

    if (openFilterOn) {
      setCommentTypesToShow([COMMENT_STATUS.UNREAD, COMMENT_STATUS.OPEN]);
      return;
    }

    if (resolvedFilterOn) {
      setCommentTypesToShow([COMMENT_STATUS.RESOLVED]);
      return;
    }
    setCommentTypesToShow([]);
  }, [resolvedFilterOn, openFilterOn]);

  // FOR TESTING ONLY
  useEffect(() => {
    if (!_.isEmpty(mockDocumentServices)) {
      setDocumentServices(mockDocumentServices);
    }
  }, [mockDocumentServices, setDocumentServices]);

  useEffect(() => {
    function switchViewMode() {
      setLoading(true);
      previewEl.current.contentWindow.documentServices.viewMode.set(
        documentAPIs.getDocumentViewMode(viewMode),
      );
      previewEl.current.contentWindow.documentServices.waitForChangesApplied(
        () => {
          setLoading(false);
        },
      );
    }
    if (
      documentServices &&
      _.get(previewEl, 'current.contentWindow.documentServices')
    ) {
      switchViewMode();
    }
  }, [documentServices, viewMode]);

  useEffect(() => {
    async function handleDSLoaded({ data }) {
      if (data === DOCUMENT_SERVICES_LOADED) {
        setIframeElement(previewEl.current);
        documentServicesRef.current =
          previewEl.current.contentWindow.documentServices;
        await documentAPIs.setSiteActions(
          previewEl.current.contentWindow.documentServices,
        );
        previewEl.current.contentWindow.documentServices.viewMode.set(
          VIEW_MODE_DOCUMENT_VALUES.DESKTOP,
        );
        setDocumentServices(previewEl.current.contentWindow.documentServices);

        dispatch({
          type: ACTIONS.SET_SITE_PAGES_IDS,
          payload: previewEl.current.contentWindow.documentServices.pages
            .getPagesList()
            .map(page => page.id),
        });
        setLoading(false);
      }
    }
    window.addEventListener('message', handleDSLoaded);
    return () => {
      window.removeEventListener('message', handleDSLoaded);
    };
  }, [previewEl, setDocumentServices]);

  useEffect(() => {
    setTimeout(() => {
      if (!documentServicesRef.current) {
        setLoading(false);
        siteDidNotRender();
      }
    }, SITE_LOADING_GRACE);
  }, [documentServices, siteDidNotRender]);

  function showIfInMobile() {
    return viewMode === VIEW_MODE.MOBILE ? s.show : s.hide;
  }

  function getMobileLeft(styleOffset) {
    if (viewMode === VIEW_MODE.MOBILE) {
      const offset = styleOffset + getScrollOffset(previewEl.current) / 2;
      return {
        left: `calc(50% - ${offset}px)`,
      };
    }
  }

  function hideIfSwitchingDevice() {
    return loading ? s.hide : s.show;
  }

  function getMobileClassIfInMobile() {
    return viewMode === VIEW_MODE.MOBILE ? s.frameMobile : '';
  }

  function getHeaderPortalRootForResponsiveSites() {
    return previewEl.current.contentDocument.querySelector('header');
  }

  function getHeaderPortalRootForClassicSites() {
    return previewEl.current.contentDocument.getElementById(DOCUMENT_HEADER);
  }

  function removeTrailingSlash(input) {
    return _.endsWith(input, '/')
      ? input.substring(0, input.length - 1)
      : input;
  }

  function isDynamicPageComment(comment) {
    const isValidStaticPageComment = isStaticPageComment(comment);
    if (!isValidStaticPageComment) {
      return false;
    }

    // dynamic page - check comment inner path vs current page inner path (input)
    const innerPath = calcCommentInnerPathFromPageSlug(
      comment.commentLocation,
      documentServices,
    );

    if (!innerPath) {
      return false;
    }
    return (
      removeTrailingSlash(`./${currentPageInnerPath}`) ===
      removeTrailingSlash(innerPath)
    );
  }

  function isStaticPageComment(comment) {
    const { breakpoint } = comment.commentLocation;
    // static page - no need to check inner paths
    if (!isSiteResponsive) {
      // classic site - check view mode: "MOBILE"\"DESKTOP"
      return breakpoint.id === viewMode;
    }
    // responsive site
    if (comment.isMigrated) {
      // old comment - backward compatible
      return true;
    }

    // comment on a page with breakpoint - responsive
    const { minWidth, maxWidth } = breakpoint;
    const { max, min } = currentBreakPoint;
    return minWidth === min && maxWidth === max;
  }
  function renderPortals() {
    const headerComments = _.filter(comments, comment => {
      const { pageId: commentPageId, breakpoint } = comment.commentLocation;
      if (!isSiteResponsive) {
        return commentPageId === DOCUMENT_HEADER && breakpoint.id === viewMode;
      } else {
        const { minWidth, maxWidth } = breakpoint;
        const { max, min } = currentBreakPoint;
        if (comment.isMigrated) {
          return commentPageId === DOCUMENT_HEADER;
        }
        return (
          commentPageId === DOCUMENT_HEADER &&
          minWidth === min &&
          maxWidth === max
        );
      }
    });
    const currentPageComments = _.filter(comments, comment => {
      const { pageId: commentPageId } = comment.commentLocation;
      if (pageId !== commentPageId) {
        // different page
        return false;
      }

      const isDynamic = isDynamicPage(commentPageId, documentServices);
      if (isDynamic === undefined) {
        // page does not exist
        return false;
      }

      if (isDynamic === true) {
        return isDynamicPageComment(comment);
      }
      return isStaticPageComment(comment);
    });
    function getPageXOffset() {
      const siteContainer =
        previewEl.current.contentDocument.getElementById(DOCUMENT_CONTAINER);
      if (siteContainer) {
        return (
          (previewEl.current.contentWindow.innerWidth -
            previewEl.current.contentDocument.getElementById(DOCUMENT_CONTAINER)
              .clientWidth) /
          2
        );
      } else {
        return 0;
      }
    }

    const maxPageHeight =
      documentServicesRef?.current?.site.getHeight() ?? Infinity;
    function renderCommentPin(comment) {
      return (
        <Suspense key={comment.id} fallback={<div></div>}>
          <FeedbackPin
            key={comment.id}
            comment={comment}
            onUpdateComment={onUpdateComment}
            onResolveComment={onResolveComment}
            onAppendOwnerReply={onAppendOwnerReply}
            onMarkCommentAsRead={onMarkCommentAsRead}
            onDeleteReplyToComment={onDeleteReplyToComment}
            onUpdateReplyToComment={onUpdateReplyToComment}
            closeAllComments={closeAllComments}
            pageYOffset={previewEl.current.contentWindow.pageYOffset}
            pageXOffset={getPageXOffset()}
            maxPageHeight={maxPageHeight}
          />
        </Suspense>
      );
    }

    return (
      <>
        <Portal
          portalRoot={
            isSiteResponsive
              ? getHeaderPortalRootForResponsiveSites()
              : getHeaderPortalRootForClassicSites()
          }
          closeAllComments={closeAllComments}
        >
          <style>{feedbackPinStyle(COMMENT_COLOR)}</style>
          {headerComments.map(comment => {
            if (_.includes(commentTypesToShow, comment.status)) {
              return renderCommentPin(comment);
            }
          })}
        </Portal>
        <Portal
          portalRoot={previewEl.current.contentDocument.getElementById(pageId)}
          closeAllComments={closeAllComments}
        >
          <style>{feedbackPinStyle(COMMENT_COLOR)}</style>
          {currentPageComments.map(comment => {
            if (_.includes(commentTypesToShow, comment.status)) {
              return renderCommentPin(comment);
            }
          })}
        </Portal>
      </>
    );
  }

  function renderClassicPreview() {
    return (
      <>
        <div
          style={getMobileLeft(186)}
          className={`${s.mobile} ${showIfInMobile()}`}
        ></div>
        <div
          style={getMobileLeft(197)}
          className={`${s.mobileBg} ${showIfInMobile()}`}
        ></div>
        <Modal isOpen={showOverlay} zIndex={1} />
        <iframe
          title="preview-classic"
          id={PREVIEW_ID}
          ref={previewEl}
          className={`${
            s.editorIframe
          } ${getMobileClassIfInMobile()} ${hideIfSwitchingDevice()}`}
          src={siteUrl}
          data-hook="site-renderer-preview"
        ></iframe>
        <div
          style={getMobileLeft(197)}
          className={`${s.mobileFooter} ${showIfInMobile()}`}
        ></div>
        {previewEl.current && documentServices && comments.length > 0
          ? renderPortals()
          : ''}
      </>
    );
  }

  function renderResponsivePreview() {
    const width =
      currentBreakPoint.max === BREAKPOINT_MAX_VALUE
        ? '100%'
        : documentAPIs.calcBreakpointWidth({ ...currentBreakPoint });
    return (
      <div style={{ margin: '0 auto', display: 'grid', width }}>
        <Modal isOpen={showOverlay} zIndex={1} />
        <iframe
          title="preview-responsive"
          id={PREVIEW_ID}
          ref={previewEl}
          style={{ width }}
          className={`${
            s.editorIframe
          } ${getMobileClassIfInMobile()} ${hideIfSwitchingDevice()}`}
          src={siteUrl}
          data-hook="site-renderer-preview"
        ></iframe>
        {previewEl.current && documentServices && comments.length > 0
          ? renderPortals()
          : ''}
      </div>
    );
  }

  return (
    <div className={s.siteRenderer} data-hook="site-renderer">
      <div className={s.iframeWrapper}>
        <div className={s.iframeContainer}>
          {isSiteResponsive
            ? renderResponsivePreview()
            : renderClassicPreview()}
        </div>
      </div>
    </div>
  );
}

export default SiteRenderer;
